import get from 'lodash/get'
import {
  Enum_Notification_Type,
  Enum_Userspermissionsuser_Status,
  Maybe,
  TaskEntity,
  UploadFileEntity
} from 'generated/graphql'
import { STRAPI_USER_STORAGE_KEY } from '../constants'

import { images } from 'theme'
import { Enum_Task_Status } from 'generated/graphql'
import axios from 'axios'
import { FiAlertTriangle, FiBell, FiClock, FiInfo } from 'react-icons/fi'

/**
 * Gets JWT of authenticatedUser from either sessionStorage or localStorage.
 *
 * Every network request goes through here, and both the read and the parse can
 * throw — storage is unavailable in Safari private mode and in some embedded
 * webviews, and a truncated/corrupted entry makes `JSON.parse` fail. Either one
 * used to take down the whole app rather than degrading to "signed out", so
 * both are handled.
 */
export const fetchJwt = (): string | null => {
  try {
    const raw =
      sessionStorage.getItem(STRAPI_USER_STORAGE_KEY) ??
      localStorage.getItem(STRAPI_USER_STORAGE_KEY)

    if (!raw) return null

    const parsed = JSON.parse(raw)
    return typeof parsed?.jwt === 'string' ? parsed.jwt : null
  } catch {
    return null
  }
}

export function formatError({ response }: any) {
  return get(response, 'data.error.details[0].messages[0].message', 'An unknown error has occured.')
}

/**
 * Normalise a project's `collaborators` value into the array-of-strings the API
 * expects, regardless of how the form/stored project provides it.
 *
 * The create-project forms bind `collaborators` as a comma-separated string, but a
 * project reloaded from the backend (e.g. when a user revisits the onboarding
 * step) returns it as an array — or nothing at all. Blindly calling `.split()` on
 * that non-string value throws and breaks the whole submit, which is why creating
 * a project could fail for anyone returning to a saved project. Handling every
 * shape here keeps the submit resilient without changing the happy-path behaviour.
 */
export const toCollaboratorsArray = (collaborators: unknown): string[] => {
  if (Array.isArray(collaborators)) {
    return collaborators.map((entry) => String(entry).trim()).filter(Boolean)
  }
  if (typeof collaborators === 'string') {
    return collaborators
      .split(',')
      .map((entry) => entry.trim())
      .filter(Boolean)
  }
  return []
}

export const getTaskStatuses = () => {
  const statuses: { [key: string]: string } = { ...Enum_Task_Status }
  return Object.keys(statuses)
    .sort()
    .map((key) => {
      return {
        label: statuses[key]?.split('_').join(' '),
        value: statuses[key]
      }
    })
}
export const statusHelper = (status: string, hex?: boolean) => {
  const statusColors: { [key: string]: string } = {
    ACTIVE: hex ? '#718096' : 'gray',
    INACTIVE: hex ? '#b77575' : 'red',
    IN_PROGRESS: hex ? '#bee3f8' : 'blue',
    REPORTED: hex ? '#bee3f8' : 'blue',
    COMPLETED: hex ? '#718096' : 'gray',
    URGENT: hex ? '#b77575' : 'red',
    CANCELLED: hex ? '#b77575' : 'red',
    REJECTED: hex ? '#b77575' : 'red',
    PENDING: hex ? '#fefbbf' : 'orange',
    MORE_INFO_REQUESTED: hex ? '#9a88be' : 'purple',
    ARCHIVED: hex ? '#edf2f7' : 'red',
    LIVE: hex ? '#9a88be' : 'purple',
    SENT: hex ? '#718096' : 'gray',
    DRAFT: hex ? '#fefbbf' : 'orange',
    RECEIVED: hex ? '#88bea0ff' : 'blue',
    DONE: hex ? '#718096' : 'gray'
  }
  const icons: { [key: string]: string } = {
    ACTIVE: 'VscCircleFilled',
    INACTIVE: 'VscClose',
    IN_PROGRESS: 'VscIssueDraft',
    REPORTED: 'VscLoading',
    COMPLETED: 'VscCheck',
    URGENT: 'VscWarning',
    CANCELLED: 'VscClose',
    REJECTED: 'VscClose',
    PENDING: 'VscIssueDraft',
    MORE_INFO_REQUESTED: 'VscEdit',
    ARCHIVED: 'VscClose',
    LIVE: 'VscCircleFilled',
    SENT: 'VscCheck',
    DRAFT: 'VscIssueDraft',
    RECEIVED: 'VscCheckAll',
    DONE: 'VscCheck'
  }
  return {
    title: status.toUpperCase().replace('_', ' '),
    color: statusColors[status] || 'gray.500',
    icon: icons[status] || 'VscCircleFilled'
  }
}
export function* arrayRange(start: number, end: number, step: number) {
  while (start < end) {
    yield start
    start += step
  }
}

export function capitalizeFirstLetter(string: string) {
  return string.charAt(0).toUpperCase() + string.slice(1)
}

export function getImageByFileExtension(fileName: string) {
  const fileExtension = fileName.split('.').pop()
  switch (fileExtension) {
    case 'csv':
      return images.csv
    case 'txt':
      return 'text.svg'
    case 'pdf':
      return images.pdf
    case 'doc':
    case 'docx':
      return images.word
    case 'xls':
    case 'xlsx':
      return images.excel
    case 'ppt':
    case 'pptx':
      return images.powerpoint
    case 'zip':
    case 'rar':
      return 'zip.svg'
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
      return 'image.svg'
    case 'mp3':
    case 'wav':
    case 'aac':
      return 'audio.svg'
    case 'mp4':
    case 'avi':
    case 'mkv':
      return 'video.svg'
    default:
      return images.logoNoText
  }
}

export function pluralHandler(string: string, count: number) {
  const y = string.endsWith('y')
  const finalString = y ? string.slice(0, -1) : string
  if (count > 1) {
    return y ? finalString + 'ies' : finalString + 's'
  }
  return string
}

export function parseDot(str: string, prefix?: string) {
  return prefix + str?.split('&dot;')?.join('.')
}

export const swapArrayLocs = (arr: any[], from: number, to: number) => {
  // remove `from` item and store it
  const f = arr.splice(from, 1)[0]
  // insert stored item into position `to`
  arr.splice(to, 0, f)
}
export const getAppPath = (location: any) =>
  [location.pathname, location.search, location.hash].join('')

/**
 * Whether a customer still needs to finish the guided (PIA) onboarding by
 * completing the pay gate (which starts their free trial and flips
 * `subscriptionStarted`/`confirmed`).
 *
 * Deliberately scoped to the automated signup flow so legacy, invited and
 * already-paying accounts are never affected, and it errs OPEN (returns false)
 * on missing/partial data so a data hiccup can never lock a paying customer out.
 * Single source of truth for the post-registration setup gate.
 */
export const needsPostRegistrationSetup = (
  attributes?: { confirmed?: boolean | null; onboardingInformation?: any } | null
): boolean => {
  const onboarding = attributes?.onboardingInformation || null
  const isAutomatedSignup = onboarding?.onboardingChannel === 'automated'
  // Either flag proves the pay gate completed — they are set together atomically
  // when the free trial starts.
  const hasStartedSubscription =
    onboarding?.subscriptionStarted === true || attributes?.confirmed === true
  return Boolean(isAutomatedSignup && !hasStartedSubscription)
}

export const getImages = (task: Maybe<TaskEntity>) => {
  let hasImageField = false
  const evedence = task?.attributes?.evidences?.data?.[0]?.attributes

  const customFields = evedence?.custom_data_fields || []
  const fieldImages = []
  for (const field of customFields) {
    if (field?.__typename === 'ComponentCustomDataFieldsImageField') {
      const images = field?.images?.data
      if (images && images?.length > 0) {
        hasImageField = true
        fieldImages.push(...images)
      }
    }
  }
  return {
    hasImages: (evedence?.photos && evedence?.photos?.data?.length > 0) || hasImageField,
    images: (evedence?.photos?.data || []).concat(fieldImages)
  }
}

export const getStatuses = () => {
  const statuses: { [key: string]: string | number } = { ...Enum_Userspermissionsuser_Status }
  return Object.keys(statuses).map((key) => {
    return { label: statuses[key], value: statuses[key] }
  })
}

export const disableReactDevTools = () => {
  const noop = () => undefined
  const DEV_TOOLS = (window as any).__REACT_DEVTOOLS_GLOBAL_HOOK__

  if (typeof DEV_TOOLS === 'object') {
    for (const [key, value] of Object.entries(DEV_TOOLS)) {
      DEV_TOOLS[key] = typeof value === 'function' ? noop : null
    }
  }
}

export const pathNameExists = (pathname: string, pathnames: string[]) => {
  return pathnames.some((path) => pathname.includes(path))
}

/**
 * A utility function that wraps axios as `fetch`.
 * There's still a lot of work to be done here, but this is a start.
 * @param url the url to fetch
 * @param method the HTTP method to use
 * @param _options any request options, and headers (to be designed)
 */
export const fetch = async (url: string, method = 'GET', _options?: any): Promise<any> => {
  if (method === 'GET') {
    const response = await axios.get(url).then((response) => {
      return response.data
    })
    return response
  } else {
    throw new Error('Method not supported')
  }
}

export const tranformObjectToQueryString = (obj: any) => {
  const str = []
  for (const p in obj)
    if (obj.hasOwnProperty(p)) {
      str.push(encodeURIComponent(p) + '=' + encodeURIComponent(obj[p]))
    }
  return str.join('&')
}

export const removeQueryParams = (url: string) => {
  return url.split('?')[0]
}

/**
 * Parse a JSON payload that may have been mangled by a mobile keyboard.
 *
 * Device-captured strings (evidence `location`, task-builder `fields`) reach us
 * with typographic quotes substituted for straight ones, so they have to be
 * normalised before parsing. The previous helper, `replaceQuotationMarks`,
 * rewrote `“ ”` to a *single* quote — which JSON does not accept — so the one
 * case it existed to handle was exactly the case that threw. Every call site
 * fed it directly into `JSON.parse`, unguarded and, in the task-detail screen,
 * during render: a single malformed blob replaced the whole page with the
 * error boundary.
 *
 * Never throws; returns `fallback` for anything unreadable.
 */
export const safeJsonParse = <T>(value: unknown, fallback: T): T => {
  if (value === null || value === undefined) return fallback
  if (typeof value === 'object') return value as T
  if (typeof value !== 'string') return fallback

  const trimmed = value.trim()
  if (!trimmed) return fallback

  // Valid JSON parses as-is. Curly double quotes are only swapped for straight
  // ones when that fails — the old blobs this exists for were written with
  // “smart” quotes as delimiters. Apostrophes are never touched: rewriting `'`
  // to `"` turned any text with an apostrophe ("Ricky's meter") into invalid
  // JSON, so a template draft holding one came back as the fallback `[]` and
  // the editor emptied the whole form.
  for (const candidate of [trimmed, trimmed.replace(/[”“]/g, '"')]) {
    try {
      const parsed = JSON.parse(candidate)
      return (parsed ?? fallback) as T
    } catch {
      // try the next candidate
    }
  }
  return fallback
}

/**
 * Whether a stored photo's mime type is something browsers can actually paint.
 *
 * iPhones uploaded raw HEIC captures for a period (see the file-upload
 * service's normalize-image.ts for the fix); no browser renders HEIC, so an
 * `<img>` pointed at one shows only a broken frame. Anything unrecognised is
 * treated as renderable — the graceful fallback is only for formats we KNOW
 * cannot work.
 */
export const isBrowserRenderableImage = (mime?: string | null) =>
  !/image\/(hei[cf]|tiff?)/i.test(mime || '')

export const getImageByFormat = (
  image: Maybe<UploadFileEntity> | undefined,
  format = 'thumbnail'
) => {
  return image?.attributes?.formats?.[format]?.url || image?.attributes?.url || images.logoNoText
}

export const randomColor = () => {
  //  exclude bright colors
  const letters = '0123456789ABCDEF'
  let color = '#'
  for (let i = 0; i < 6; i++) {
    color += letters[Math.floor(Math.random() * 16)]
  }
  return color
}

export const getIconAndColorForNotificationType = (type: Enum_Notification_Type) => {
  switch (type) {
    case Enum_Notification_Type.EarlyWarning:
      return { icon: FiAlertTriangle, color: 'red.300' }
    case Enum_Notification_Type.General:
      return { icon: FiBell, color: 'blue.300' }
    case Enum_Notification_Type.MoreInfoRequested:
      return { icon: FiInfo, color: 'yellow.300' }
    case Enum_Notification_Type.Reminder:
      return { icon: FiClock, color: 'gray.300' }
    default:
      return { icon: FiBell, color: 'blue.300' }
  }
}
